import { Button, Dialog, DialogContent, DialogTitle } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React from "react";
import { Site } from "types/portfolio";
import { AgGridReact } from "ag-grid-react";
import { CellClassParams, GridOptions, RowSpanParams } from "ag-grid-community";
import { getContextMenu, rowSpanning } from "utils";
import cloneDeep from "lodash/cloneDeep";
import HealthScoreRenderer from "components/portfolio/cellRenderer/HealthScoreRenderer";
import { OrgId } from "types/dashboard/enterprise";
import { AxiosInstance } from "axios";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    right: 0,
    top: theme.spacing(2),
    color: "grey",
  },
  capitalize: {
    textTransform: "capitalize",
  },
}));

export interface SiteURLsDialogProps {
  siteURLsHandleClose: () => void;
  siteCellData: any;
  siteDialogStatus: boolean;
  type?: string;
  axios?: AxiosInstance;
  accountType?: string;
  subType?: string;
}

function getSortedData<T extends Site | OrgId>(
  data: T[],
  by?: { colId: keyof T; sort: "asc" | "desc" }
) {
  const localData = cloneDeep(data);
  if (localData?.length) {
    localData?.sort((a, b) => {
      const compareId = (a.ciOrgId ?? "N/A").localeCompare(b.ciOrgId ?? "N/A");
      const compareBy = by
        ? by.sort === "asc"
          ? ((a[by.colId] ?? "") as string).localeCompare((b[by.colId] ?? "") as string)
          : ((b[by.colId] ?? "") as string).localeCompare((a[by.colId] ?? "") as string)
        : 0;

      return compareId !== 0 ? 0 : compareId || compareBy;
    });
    return rowSpanning(localData, "ciOrgId") as T[];
  } else {
    return [];
  }
}

const comparator = () => 0;

const SiteURLsDialog: React.FC<SiteURLsDialogProps> = ({
  siteCellData,
  siteURLsHandleClose,
  siteDialogStatus,
  type,
  axios,
  accountType,
  subType,
}) => {
  const classes = useStyles();
  const rowData = getSortedData(siteCellData);
  const config: GridOptions = {
    columnDefs: [
      {
        headerName: "Control Hub Organization ID",
        field: "ciOrgId",
        colId: "ciOrgId",
        headerTooltip: "Common Identity ID associated to the respective Webex Site URLs",
        valueGetter: (params) => params?.data?.ciOrgId ?? "N/A",
        rowSpan: (params: RowSpanParams) => params.data?.rowSpan,
        cellClassRules: {
          "cell-span": (params: CellClassParams) => params.data?.rowSpan !== 1,
          "cell-noSpan": (params: CellClassParams) => params.data?.rowSpan === 1,
          "cont-span": (params: CellClassParams) => !params.data?.groupFlag,
        },
        pinned: "left",
        suppressMovable: true,
        lockPinned: true,
        lockPosition: true,
      },
      {
        headerName: "Site URLs",
        colId: "siteName",
        field: "siteName",
        valueGetter: (params) => {
          const data: Site = params.data;
          if (data.siteName) {
            if (data.primarySite) {
              return `${data.siteName} (Primary)`;
            } else {
              return data.siteName;
            }
          } else {
            return "N/A";
          }
        },
        comparator,
      },

      {
        headerName: "Cluster",
        colId: "clusterCode",
        valueGetter: (params) => {
          const data: Site = params.data;
          if (data.clusterCode && data.clusterDataCenter) {
            return `${data.clusterCode} - (${data.clusterDataCenter})`;
          }
          return "";
        },
        headerTooltip: "Name of the cluster where the customer's primary Webex site resides",
        comparator,
      },
      {
        headerName: "Desktop App Version",
        colId: "desktopAppVersion",
        field: "desktopAppVersion",
        headerTooltip: "Desktop App Version associated to the customer's primary Webex site",
        comparator,
      },
    ],
    defaultColDef: {
      resizable: true,
      sortable: true,
      minWidth: type !== "renewals" && type !== "enterprise" ? 230 : 200,
    },
    tooltipShowDelay: 0,
    rowData,
    getContextMenuItems: getContextMenu,
    suppressRowTransform: true,
    onGridReady: (params) => {
      const gridApi = params.columnApi;
      const allColumnIds: string[] = [];
      gridApi.getAllColumns().forEach((column) => {
        allColumnIds.push(column.getId());
      });
      gridApi.autoSizeColumns(allColumnIds, false);
    },
    onSortChanged(event) {
      const { api } = event;
      const rowModel = api.getSortModel();
      //@ts-ignore
      const data = getSortedData(siteCellData, rowModel?.[0]);
      api.setRowData(data);
    },
    frameworkComponents: {
      HealthScoreRenderer,
    },
  };
  if (type !== "renewals" && type !== "enterprise") {
    config.columnDefs?.push(
      ...[
        {
          headerName: "WSMP Meetings Flag",
          colId: "wsmpFlag",
          field: "wsmpFlag",
          valueGetter: (params: any) => {
            const data: Site = params.data;
            return data?.wsmpFlag ? (data?.wsmpFlag === "N" ? "No" : "Yes") : "N/A";
          },
          comparator: (a: string, b: string) => a.localeCompare(b),
        },
        {
          headerName: "WSMP Meetings Migration Date",
          colId: "wsmpDate",
          field: "wsmpDate",
          valueGetter: (params: any) => {
            const data: Site = params.data;
            return data.wsmpDate ? moment(data?.wsmpDate).format("MM/DD/YYYY") : "N/A";
          },
          comparator: (a: string, b: string) => moment(a).diff(moment(b)),
        },
        {
          headerName: "OneView Health Score",
          colId: "healthScore",
          field: "healthScore",
          valueGetter: (params: any) => {
            const data: Site = params.data;
            return data?.healthScore >= 0 && data.healthScore !== null
              ? data?.healthScore?.toFixed(1) + "%"
              : "N/A";
          },
          cellRenderer: "HealthScoreRenderer",
          cellRendererParams: {
            type: type,
            axios,
            rowData,
            accountType,
          },
          headerTooltip: `<div style="height: 300px;overflow-y: scroll">
      <p>The Health Score listed here is: </p>
      <p>1.the score of the primary Webex site URL under an account</p>
      <p>2.Case when no sites associated to an account, then the score is associated to the Control Hub (CH) Organization ID linked to the account</p>
      <p>3.Case when multiple Control Hub Organization ID's are linked to one account, then the score is of the CH Org ID that maps to a subscription having the highest Annual Recurring Revenue (ARR).</p>
      <p>A primary site is the one with highest count of 'Active' users in last 30 days.</p>
      <p>To view the score of other sites or CH Org IDs associated to the account, select 'Show All' under 'Site URLs' or 'Control Hub Organization ID' column or view them on the 'Details' page under 'Sites' section. The score is derived considering following contributory scores:</p>
      <p>- Org Settings Score</p>
      <p>- User Management Score</p>
      <p>- Integration Score</p>
      <p>
        - Meetings (Usage and Adoption Score, Join Meeting Time Score, In Meeting
        Score)
      </p>
      <p>- Rooms (Usage and Adoption Score, Quality Score)</p>
      <p>- Messaging (Usage and Adoption Score)</p>
      <p>- Calling (Usage and Adoption Score)</p>
      </div>`,
        },
        {
          headerName: "AI Enabled Flag",
          colId: "aiEnabledFlag",
          field: "aiEnabledFlag",
          valueGetter: (params: any) => {
            const data: Site = params.data;
            return data?.aiEnabledFlag ? (data?.aiEnabledFlag === "N" ? "No" : "Yes") : "N/A";
          },
          comparator: (a: string, b: string) => a.localeCompare(b),
        },
        {
          headerName: "AI Enabled Date",
          colId: "aiEnabledDate",
          field: "aiEnabledDate",
          valueGetter: (params: any) => {
            const data: Site = params.data;
            return data.aiEnabledDate ? moment(data?.aiEnabledDate).format("MM/DD/YYYY") : "N/A";
          },
          comparator: (a: string, b: string) => moment(a).diff(moment(b)),
        },
      ]
    );
  }
  return (
    <>
      <Dialog
        open={siteDialogStatus}
        onClose={siteURLsHandleClose}
        fullWidth={true}
        maxWidth={type !== "renewals" && type !== "enterprise" ? "lg" : "md"}
      >
        <DialogTitle>
          {type === "renewals"
            ? `${
                subType === "site" ? "Site URLs" : "Control Hub Organization ID"
              }(Webex site(s) associated to this subscription only)`
            : type === "ciOrgId" || type === "enterprise"
              ? "Control Hub Organization ID"
              : "Site URLs"}
          <Button
            className={classes.closeButton}
            onClick={siteURLsHandleClose}
          >
            x
          </Button>
        </DialogTitle>
        <DialogContent dividers={true}>
          <div
            className="ag-theme-material"
            style={{
              height: "40vh",
              width: "100%",
            }}
          >
            <AgGridReact {...config} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SiteURLsDialog;
